import { useCallback } from "react";
import type { SpecsEditorSession } from "./types";
import { resolveFalImageInputUrls } from "./specsImageUpload";

const FAL_GPT_IMAGE_2_EDIT_MODEL = "openai/gpt-image-2/edit";
const FAL_GPT_IMAGE_2_EDIT_ENDPOINT = "https://fal.run/openai/gpt-image-2/edit";

export function useSpecsEditorFalCalls() {
  const callFal = useCallback(
    async (
      prompt: string,
      imageUrls: string[],
      settings: SpecsEditorSession["falSettings"]
    ): Promise<{ images: { url: string }[] } | null> => {
      if (!settings.apiKey) throw new Error("fal.ai API key is required");

      const { getCached, setCached } = await import("./falCache");
      const cacheKey = imageUrls[0] ?? "";
      if (!cacheKey) throw new Error("An input image is required");

      const cached = await getCached(
        cacheKey,
        prompt,
        FAL_GPT_IMAGE_2_EDIT_MODEL
      );
      if (cached) return { images: [{ url: cached }] };
      const falImageUrls = await resolveFalImageInputUrls(imageUrls);

      const response = await fetch(FAL_GPT_IMAGE_2_EDIT_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Key ${settings.apiKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt,
          image_urls: falImageUrls,
          image_size: "auto",
          quality: "low",
          num_images: 1,
          output_format: "png",
        }),
      });

      if (!response.ok) {
        throw new Error(
          `fal.ai error ${response.status}: ${await response.text()}`
        );
      }
      const result = await response.json();
      const resultUrl = result?.images?.[0]?.url;
      if (resultUrl) {
        setCached(cacheKey, prompt, FAL_GPT_IMAGE_2_EDIT_MODEL, resultUrl);
      }
      return result;
    },
    []
  );

  return { callFal };
}
